import {
  PropsWithChildren,
  ReactElement,
  createContext,
  useContext,
  useMemo,
  useEffect,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMe } from '~/services/membersService'
import useCorrelationId from '~/hooks/useCorrelationId/useCorrelationId'
import { datadogRum } from '~/utils/datadog'
import { ApiRouteRequestError, PlatformError } from '~/utils/errors'
import useChatWidget from '~/hooks/useChatWidget/useChatWidget'
import { MemberDataTransferObject } from '@reward-platform/ancillaries-schemas/members'
import { CountryCodes, CurrencyCodes } from '@reward-platform/platform-config'
import { useAuthContext } from '../auth'
import useUserHeaders from './useUserHeaders'
import useUserLocaleResolver from './useUserLocaleResolver'
import { UserContextValue } from './User.types'
import { useNotification } from '../notification'

const DEFAULT_USER: UserContextValue = {
  userCurrency: 'GBP',
  userCountry: 'GB',
  userFailureCount: 0,
}

export const UserContext = createContext<UserContextValue | undefined>(DEFAULT_USER)

UserContext.displayName = 'UserContext'

export const UserProvider = ({
  children,
}: PropsWithChildren): ReactElement<React.PropsWithChildren> => {
  const { addErrorCodeNotification } = useNotification()
  const { correlationId } = useCorrelationId()
  const { isAuthenticated, userId } = useAuthContext()
  useChatWidget()

  const {
    data: user,
    error,
    isError,
    isLoading,
    failureCount,
  } = useQuery<MemberDataTransferObject, ApiRouteRequestError>({
    queryKey: ['getUser', userId],
    queryFn: () => getMe(),
    enabled: isAuthenticated,
  })

  const { memberIdentifier } = user || {}

  const { countryCode, currencyCode } = useUserHeaders(user)
  useUserLocaleResolver(user)

  const headersSet = !!countryCode && !!currencyCode

  useEffect(() => {
    if (memberIdentifier?.identifier) {
      datadogRum.setUser({
        id: memberIdentifier.identifier,
        correlationId,
      })
    }
  }, [correlationId, memberIdentifier?.identifier])

  useEffect(() => {
    if (isError && error) {
      addErrorCodeNotification('USER_CONTEXT_ERROR', {
        type: 'ERROR',
        action: 'RELOAD',
        errorCode: error.code,
        statusCode: error.httpStatus,
      })
    }
  }, [isError, error, addErrorCodeNotification])

  const contextValue = useMemo<UserContextValue>(
    () => ({
      user: headersSet ? user : undefined,
      isLoading,
      userCurrency: currencyCode as CurrencyCodes,
      userCountry: countryCode as CountryCodes,
      userFailureCount: failureCount ?? 0,
    }),
    [headersSet, user, isLoading, currencyCode, countryCode, failureCount]
  )

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}

export function useUser(): UserContextValue {
  const context = useContext(UserContext)
  if (!context) {
    throw new PlatformError('useUser must be used within a UserProvider')
  }
  return context ?? {}
}
